import React from "react";
import { Stack } from "@mui/system";
import { useTranslations } from "next-intl";
import Add from "@mui/icons-material/Add";
import AddBox from "@mui/icons-material/AddBox";
import dayjs from "dayjs";
import { Button, IconButton, InputAdornment } from "@mui/material";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@cbex/utils/theme";
import { AppContext } from "@cbex/utils/context";
import { WcDateRangePicker, WcTextField } from "@bpipartners/webui";
import { MdClose, MdSearch } from "react-icons/md";
import { ActivityFilterValues } from "../utils/helperFunctions";

interface ActivityFilterComponentProps {
  inputValues?: ActivityFilterValues;
  onDataChanged: (inputParams: ActivityFilterValues) => void;
  isActivityOverview: boolean;
  isNotCandidateHistory?: boolean;
  onNewPressed?: () => void;
  onPublicExamPressed?: () => void;
  onSearchChanged?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ActivityFilterComponent = (
  props: ActivityFilterComponentProps
) => {
  const {
    inputValues,
    isActivityOverview,
    onNewPressed,
    onDataChanged,
    isNotCandidateHistory,
    onPublicExamPressed,
    onSearchChanged,
  } = props;
  const t = useTranslations();
  const theme = useTheme();
  //@ts-ignore
  const { localAppState } = React.useContext(AppContext);
  const { enableUseOpenExam, enableNewExam } =
    localAppState?.userData?.organization?.properties;
  const [searchText, setSearchText] = React.useState("");
  const smallDevice = useMediaQuery(theme.breakpoints.down("sm"));
  const [filterValues, setFilterValues] = React.useState<ActivityFilterValues>({
    //@ts-ignore
    dateFrom: dayjs() || inputValues.dateFrom,
    //@ts-ignore
    dateTill: dayjs().add(3, "month") || inputValues.dateTill,
  });

  React.useEffect(() => {
    if (inputValues) {
      setFilterValues(inputValues);
    }
  }, [inputValues]);

  const handleDateRange = (input: any[]) => {
    setFilterValues({
      ...filterValues,
      dateFrom: input[0],
      dateTill: input[1],
    });
    onDataChanged({
      ...filterValues,
      dateFrom: input[0],
      dateTill: input[1],
    });
  };

  return (
    <Stack
      id="ActivityFilterComponent"
      height={theme.dimensions.topbarHeight}
      spacing={2}
      direction={"row"}
      width="100%"
      alignItems={"center"}
    >
      <Stack>
        <WcDateRangePicker
          controlDisabled={false}
          width={300}
          label={t("labels.dateRangeFilter")}
          value={[filterValues.dateFrom, filterValues.dateTill]}
          minDate={
            isActivityOverview ? dayjs().subtract(3, "month") : undefined
          }
          maxDate={isActivityOverview ? undefined : dayjs().add(3, "month")}
          onChange={(newValue) => {
            //@ts-ignore
            handleDateRange(newValue);
          }}
        />
      </Stack>

      <Stack
        width={{ md: "100%", sm: "40%" }}
        alignSelf={"flex-end"}
        height={"100%"}
        alignItems={"center"}
        flexDirection={"row"}
      >
        {isNotCandidateHistory && (
          <Stack>
            <WcTextField
              placeholder={t("labels.searchText")}
              sx={{ width: 300 }}
              value={searchText}
              onChange={(event) => {
                setSearchText(event.target.value);
                //@ts-ignore
                onSearchChanged(event);
              }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <MdSearch size={24} />
                    </InputAdornment>
                  ),

                  endAdornment: (
                    <InputAdornment position="end">
                      {searchText.length >= 1 && (
                        <IconButton
                          onClick={() => {
                            setSearchText("");
                            //@ts-ignore
                            onSearchChanged(null);
                          }}
                        >
                          <MdClose />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Stack>
        )}
        {isActivityOverview && enableNewExam && (
          <Stack width={{ md: "80%", sm: "40%" }} direction={"row-reverse"}>
            {!smallDevice && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={onNewPressed}
                startIcon={<Add />}
              >
                {t("common.newExam")}
              </Button>
            )}
            {smallDevice && (
              <IconButton disableRipple onClick={onNewPressed}>
                <AddBox
                  style={{
                    fontSize: 30,
                  }}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                />
              </IconButton>
            )}
          </Stack>
        )}

        {isActivityOverview && enableUseOpenExam && (
          <Stack width={{ md: "60%", sm: "30%" }} direction={"row-reverse"}>
            {!smallDevice && (
              <Button
                variant="contained"
                size="small"
                color="primary"
                onClick={onPublicExamPressed}
                startIcon={<Add />}
              >
                {t("common.publicExam")}
              </Button>
            )}
            {smallDevice && (
              <IconButton disableRipple onClick={onPublicExamPressed}>
                <AddBox
                  style={{
                    fontSize: 30,
                  }}
                  sx={{
                    color: theme.palette.primary.main,
                  }}
                />
              </IconButton>
            )}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
};

export default ActivityFilterComponent;
